import { computed, observable, reaction } from "mobx";
import {
  getSnapshot,
  isoStringToDateTransform,
  Model,
  type ModelClass,
  type ModelCreationData,
  modelAction,
  prop,
  type SnapshotOutOf,
} from "mobx-keystone";
import { getStoreConfig } from "../../../config";
import { getEnv } from "../../../env";
import type { IsGlobal } from "../../../utils/types";
import type RootStore from "../../root";
import type { CoreError } from "../error";

const globalModelStorageKey = (modelType: string) => `GLOBAL-${modelType}`;

export abstract class GlobalTModel
  extends Model(
    {
      lastSync: prop<string>().withTransform(isoStringToDateTransform()),
    },
    {},
  )
  implements IsGlobal
{
  isGlobal: true = true;

  @observable
  errors: CoreError[] = [];

  creationError = false;

  @observable
  initialized = false;
  protected abstract readonly omittedKeys: (keyof SnapshotOutOf<this>)[];

  @computed
  get ready(): boolean | CoreError[] {
    if (this.creationError) {
      return this.errors;
    }

    return this.initialized;
  }

  @modelAction
  setLastSync() {
    this.lastSync = new Date();
  }

  protected onAttachedToRootStore(_root: RootStore): (() => void) | void {
    const error = this.typeCheck();
    if (!error) {
      this.initialized = true;
    }

    if (getEnv().persistLocally) {
      let callbackId: number | null = null;

      const reactionDisposer = reaction(
        () => getSnapshot(this),
        (snap) => {
          this.omittedKeys.forEach((key) => {
            delete snap[key];
          });

          void getStoreConfig()
            .storageAdapter.set(
              this.$modelType,
              globalModelStorageKey(this.$modelType),
              snap,
            )
            .finally(() => {
              callbackId = null;
            });
        },
        {
          fireImmediately: true,
          name: `${this.$modelType}-disposer`,
          scheduler: (run) => {
            if (callbackId) {
              cancelIdleCallback(callbackId);
            }
            callbackId = requestIdleCallback(run);
          },
        },
      );

      return reactionDisposer;
    }
  }
}

export type GlobalModelDeclaration<T extends GlobalTModel> = ModelClass<T>;

export async function createGlobalModel<T extends GlobalTModel>(
  _Model: GlobalModelDeclaration<T>,
  defaultData?: ModelCreationData<T>,
): Promise<T> {
  // sorry
  const ModelPrototype = Model as unknown as { $modelType: string };
  const model = await getStoreConfig().storageAdapter.get<ModelCreationData<T>>(
    ModelPrototype.$modelType,
    globalModelStorageKey(ModelPrototype.$modelType),
  );

  if (model) {
    try {
      const globalModel = new _Model({
        ...defaultData,
        ...model,
        lastSync: new Date(model.lastSync ?? 0),
      });

      globalModel.creationError = false;
      globalModel.initialized = true;

      return globalModel;
    } catch (e) {
      window.console.error(e);
    }
  }

  const defaultGlobalModel = new _Model(defaultData ?? {});

  defaultGlobalModel.initialized = true;
  defaultGlobalModel.creationError = true;

  return defaultGlobalModel;
}
